import {AfterViewChecked, Component, ElementRef, OnDestroy, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {ActivatedRoute} from "@angular/router";
import {CurrencyPipe, Location} from '@angular/common';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ToastrService} from 'ngx-toastr';
import {calculateEventDiscount, CardPaymentDTO, EventBookingDTO, EventBookingOption, EventCommitteeMember, EventDTO, EventEmailMessageDTO, EventFormAnswer, EventFormQuestion, EventParticipantDTO, EventParticipantType, EventRegistrationDTO, EventService, EventSpeaker, EventTicketOption, isDiscountActive, isStripePaymentEnabled} from '../api/event-api.service';
import {buildFeeLines, FeeCategory, FeeLine, getUnitFee, sumFeeLines} from '../event-fee-breakdown';
import {SpinnerService} from '../shared/services/spinner.service';
import {finalize, forkJoin, Observable, of, Subject, switchMap} from 'rxjs';
import {PaymentApiService} from "../api/payment-api.service";
import {UserApiService} from "../api/user-api.service";
import {CdkDragDrop, moveItemInArray} from '@angular/cdk/drag-drop';
import {answerLabelsFor, cleanQuestions, defaultAttendingQuestion, formatAnswers, missingRequiredLabels, pruneAnswers} from '../registration-questions/registration-questions.util';
import {ChapterApiService} from "../api/chapter-api.service";
import {User} from "../models/user.model";
import {toHTML} from 'ngx-editor';
import {Swiper} from 'swiper';
import {SwiperOptions} from 'swiper/types';
import {DataTableDirective} from 'angular-datatables';
import {escapeHtml, ranaDtOptions, RanaDtOptions} from '../shared/rana-datatable-defaults';
import {RoleScope, reachesAllChapters, reachesChapter, storedRoleScope} from '../shared/access';

declare const $: any;

@Component({
    selector: 'app-event-home',
    templateUrl: './event-home.component.html',
    styleUrls: ['./event-home.component.css'],
    providers: [CurrencyPipe]
})
export class EventHomeComponent implements OnInit, OnDestroy, AfterViewChecked {
    // Three independent DataTables instances (conference bookings, donations, registered
    // attendees) — only the ones relevant to the event's category/tab ever actually render.
    @ViewChild('bookingsDt', {read: DataTableDirective}) bookingsDtElement!: DataTableDirective;
    @ViewChild('bookingActionTpl') bookingActionTpl!: TemplateRef<any>;
    bookingsDtOptions: RanaDtOptions = ranaDtOptions({columns: []});
    bookingsDtTrigger: Subject<any> = new Subject();

    @ViewChild('donationsDt', {read: DataTableDirective}) donationsDtElement!: DataTableDirective;
    donationsDtOptions: RanaDtOptions = ranaDtOptions({columns: []});
    donationsDtTrigger: Subject<any> = new Subject();

    @ViewChild('regsDt', {read: DataTableDirective}) regsDtElement!: DataTableDirective;
    @ViewChild('refTpl') refTpl!: TemplateRef<any>;
    @ViewChild('regsActionTpl') regsActionTpl!: TemplateRef<any>;
    regsDtOptions: RanaDtOptions = ranaDtOptions({columns: []});
    regsDtTrigger: Subject<any> = new Subject();
    eventId!: string;
    status!: string;
    page: number = 4;
    event!: EventDTO;
    editEvent = false;
    eventForm: FormGroup;
    showRegister = false;
    userId: string = sessionStorage.getItem('userId') || '';
    userRole = JSON.parse(sessionStorage.getItem('rolesPermission') || '{}');
    userGroup = sessionStorage.getItem('userGroup') || '';
    roleScope: RoleScope = storedRoleScope();

    /** True for roles whose reach is every chapter (system administrator and the NEC tier), as decided by the API. */
    get allChapters(): boolean {
        return reachesAllChapters(this.roleScope);
    }
    userChapter = sessionStorage.getItem('chapter');
    userRegistration: any | null = null;
    eventChapterName = '';
    fromEmailOptions: string[] = [ 'events@rajputra.org'];
    pendingPaymentReferenceDrafts: { [key: string]: string } = {};

    eligibleUsers: User[] = [];

    @ViewChild('bookingFormSection') bookingFormSectionRef!: ElementRef;

    // CONFERENCE booking state
    userBooking: EventBookingDTO | null = null;
    allBookings: EventBookingDTO[] = [];
    showBookingForm = false;
    bookingQuantities: { [optionId: string]: number } = {};
    bookingDonationAmt: number = 0;
    bookingAttending: 'YES' | 'NO' | '' = '';
    // Answers to the organizer-defined registration questions (CONFERENCE booking form / LOCAL registration form).
    bookingFormAnswers: EventFormAnswer[] = [];
    // Stripe's view of each card payment of this event (organizers only), newest first.
    cardPayments: CardPaymentDTO[] = [];
    localFormAnswers: EventFormAnswer[] = [];
    localSubmitAttempted = false;
    // Edit form: question editor state. Legacy events (no registrationQuestions) are left untouched unless edited.
    editQuestions: EventFormQuestion[] = [];
    editQuestionsLegacy = false;
    editQuestionsDirty = false;
    bookingPaymentType: string = 'later';
    bookingPaymentReference: string = '';
    bookingUserNames: { [key: string]: string } = {};
    bookingUserEmails: { [key: string]: string } = {};
    bookingUserPhones: { [key: string]: string } = {};
    bookingUserCities: { [key: string]: string } = {};
    bookingUserStates: { [key: string]: string } = {};
    bookingSubmitAttempted: boolean = false;

    registrationForm = this.fb.group({
        adults: [0],
        children: [0],
        seniors: [0],
        childAges: [''],
        donationAmt: [0],  // optional donation
        paymentType: ['later', Validators.required],
        paymentReference: ['']
    });

    constructor(
        private route: ActivatedRoute,
        private fb: FormBuilder,
        private eventApi: EventService,
        private toastr: ToastrService,
        private spinner: SpinnerService,
        private paymentApi: PaymentApiService,
        private userApiService: UserApiService,
        private eventService: EventService,
        private chapterApiService: ChapterApiService,
        private location: Location,
        private currencyPipe: CurrencyPipe,
    ) {
        this.eventForm = this.fb.group({
            title: ['', Validators.required],
            eventDescription: [''],
            eventStartDate: ['', Validators.required],
            eventStartTime: ['', Validators.required],
            eventEndDate: ['', Validators.required],
            eventEndTime: ['', Validators.required],
            status: ['', Validators.required],
            eventFeeAdult: [''],
            eventFeeChild: [''],
            eventFeeSeniorCitizen: [''],
            childAgeMin: ['5'],
            childAgeLimit: [''],
            discountLabel: [''],
            discountType: ['FLAT'],
            discountValue: [''],
            discountStartDate: [''],
            discountEndDate: [''],
            fromEmail: ['events@rajputra.org'],
            additionalEmailIds: [''],
            ccEmailIds: [''],
            eventCategory: [''],
            notifyLandingPage: [false],
            zellePaymentDetails: [''],
            checkPaymentDetails: [''],
            stripePaymentEnabled: [true]
        });
    }

    editEventPanels: { label: string; options: EventTicketOption[]; groupRequired?: boolean }[] = [];

    get isEditConferenceEvent(): boolean {
        return this.eventForm.get('eventCategory')?.value === 'CONFERENCE';
    }

    addEditPanel(): void {
        this.editEventPanels.push({label: '', options: [], groupRequired: false});
    }

    removeEditPanel(i: number): void {
        this.editEventPanels.splice(i, 1);
    }

    dropEditPanel(event: CdkDragDrop<{ label: string; options: EventTicketOption[] }[]>): void {
        moveItemInArray(this.editEventPanels, event.previousIndex, event.currentIndex);
    }

    // See ChapterEventComponent.trackByIndex — cdkDropList physically repositions DOM nodes
    // during its drag animation, which desyncs ngModel-bound inputs from identity-tracked ngFor
    // items. Tracking by index keeps Angular's bindings aligned with what CDK already moved.
    trackByIndex(index: number): number {
        return index;
    }

    addEditOptionToPanel(panel: { label: string; options: EventTicketOption[] }): void {
        panel.options.push({label: '', price: '', paid: true});
    }

    removeEditOptionFromPanel(panel: { label: string; options: EventTicketOption[] }, i: number): void {
        panel.options.splice(i, 1);
    }

    private flattenEditPanels(): EventTicketOption[] {
        return this.editEventPanels.flatMap(p => p.options.map(opt => ({
            ...opt,
            sectionLabel: p.label,
            price: opt.paid === false ? '' : opt.price,
            groupRequired: !!p.groupRequired
        })));
    }

    private buildEditPanels(options: EventTicketOption[]): void {
        const map = new Map<string, EventTicketOption[]>();
        const order: string[] = [];
        for (const opt of options) {
            const key = opt.sectionLabel || '';
            if (!map.has(key)) { map.set(key, []); order.push(key); }
            map.get(key)!.push(opt);
        }
        this.editEventPanels = order.map(label => ({
            label,
            options: map.get(label)!,
            groupRequired: map.get(label)!.some(o => o.groupRequired)
        }));
    }

    // -------------------- Event Committee / Guests of Honor / Speakers --------------------
    // Backed by the EventParticipant table (see event-api.service.ts) rather than embedded on Event.
    // "Editable*" local shapes add `id` (the participant row id, absent for a not-yet-saved new row)
    // so updateEvent()/saveAllParticipants() can reconcile adds/updates/deletes on save.
    displayEventCommittee: EventCommitteeMember[] = [];
    displayEventGuestsOfHonor: EventCommitteeMember[] = [];
    displayEventSpeakers: EventSpeaker[] = [];
    isAssignedEventCommittee = false; // true when the current user is themselves a COMMITTEE participant on this event
    chapterMembers: { id: string; name: string; email: string }[] = []; // for the "link to member account" picker on committee rows

    editEventCommittee: (EventCommitteeMember & { id?: string })[] = [];
    editEventGuestsOfHonor: (EventCommitteeMember & { id?: string })[] = [];
    editEventSpeakers: (EventSpeaker & { id?: string })[] = [];

    addEditCommitteeMember(): void {
        this.editEventCommittee.push({name: '', title: '', shortDescription: ''});
    }

    removeEditCommitteeMember(i: number): void {
        this.editEventCommittee.splice(i, 1);
    }

    addEditGuestOfHonor(): void {
        this.editEventGuestsOfHonor.push({name: '', title: '', shortDescription: ''});
    }

    removeEditGuestOfHonor(i: number): void {
        this.editEventGuestsOfHonor.splice(i, 1);
    }

    addEditSpeaker(): void {
        this.editEventSpeakers.push({name: '', role: '', company: '', topic: ''});
    }

    removeEditSpeaker(i: number): void {
        this.editEventSpeakers.splice(i, 1);
    }

    // True for NEC/Chapter administrators — the only ones allowed to add/remove/edit who is on the
    // event committee itself (see the backend guard in EventParticipantServiceImpl). Guests of honor
    // and speakers aren't restricted this way — see canManageEvent below.
    //
    // NEC/NEC_PRESIDENT manage events org-wide, across every chapter — userChapter is just their own
    // home-chapter affiliation and must NOT be compared against the event's chapterId for them, or an
    // NEC officer affiliated with chapter 9 would be locked out of a chapter 5 event. Chapter-level
    // roles (CHAPTER_PRESIDENT, CEC) are scoped to their own chapter's events only.
    get isNecExecutive(): boolean {
        return this.allChapters;
    }

    get canManageCommitteeRoster(): boolean {
        return this.isNecExecutive
            || !!(this.userRole?.EVENT?.includes('CREATE') && this.userChapter === this.event?.chapterId);
    }

    // True when the logged-in user either holds the event-management role for this event's chapter,
    // or was individually assigned as an event-committee participant on this one event.
    get canManageEvent(): boolean {
        return this.canManageCommitteeRoster || this.isAssignedEventCommittee;
    }

    private loadParticipants(): void {
        this.eventApi.getEventParticipants(this.eventId).subscribe({
            next: (participants) => {
                const committee = participants.filter(p => p.participantType === 'COMMITTEE');
                const guests = participants.filter(p => p.participantType === 'GUEST_OF_HONOR');
                const speakers = participants.filter(p => p.participantType === 'SPEAKER');

                this.displayEventCommittee = committee.map(p => this.toDisplayMember(p));
                this.displayEventGuestsOfHonor = guests.map(p => this.toDisplayMember(p));
                this.displayEventSpeakers = speakers.map(p => this.toDisplaySpeaker(p));

                this.editEventCommittee = committee.map(p => ({...this.toDisplayMember(p), id: p.id}));
                this.editEventGuestsOfHonor = guests.map(p => ({...this.toDisplayMember(p), id: p.id}));
                this.editEventSpeakers = speakers.map(p => ({...this.toDisplaySpeaker(p), id: p.id}));

                this.committeeAtLoad = this.editEventCommittee.map(m => ({...m}));
                this.guestsAtLoad = this.editEventGuestsOfHonor.map(m => ({...m}));
                this.speakersAtLoad = this.editEventSpeakers.map(s => ({...s}));

                this.isAssignedEventCommittee = committee.some(p => !!p.userId && p.userId === this.userId);
                // canManageEvent is accurate only now that the committee is known
                this.loadCardPayments();
            },
            error: () => this.toastr.error('Failed to load event committee/speakers/guests of honor')
        });
    }

    // Deliberately org-wide, not scoped to this event's chapter — see the matching comment in
    // chapter-event.component.ts for why (scoping this excluded valid members and let a typed-but-
    // unselected name silently save with no linked account).
    private loadChapterMembersForPicker(): void {
        this.userApiService.getUsers().subscribe({
            next: (users: any[]) => {
                this.chapterMembers = (users || []).map(u => ({id: u.id, name: `${u.firstname} ${u.lastname}`, email: u.email || ''}));
            },
            error: () => {
            }
        });
    }

    private toDisplayMember(p: EventParticipantDTO): EventCommitteeMember {
        return {
            memberId: p.userId,
            name: p.name,
            title: p.title,
            shortDescription: p.shortDescription,
            photoUrl: p.photoUrl,
            videoUrl: p.videoUrl
        };
    }

    private toDisplaySpeaker(p: EventParticipantDTO): EventSpeaker {
        return {
            speakerId: p.id,
            name: p.name,
            role: p.title,
            company: p.company,
            topic: p.topic,
            topicDescription: p.topicDescription,
            tags: p.tags,
            photoUrl: p.photoUrl,
            videoUrl: p.videoUrl
        };
    }

    // Diffs an edited list against the ids that existed at load time, returning one add/update/delete
    // API call per changed row. Blank (never-filled-in) new rows are skipped rather than saved.
    private buildParticipantOps(original: any[], edited: any[], type: EventParticipantType): Observable<any>[] {
        const ops: Observable<any>[] = [];
        const remainingIds = new Set<string>();
        const originalById = new Map(original.filter(o => o.id).map(o => [o.id, o]));

        for (const item of edited) {
            if (!item.name || !item.name.trim()) continue;
            const dto = this.toParticipantDTO(item, type);
            if (item.id) {
                remainingIds.add(item.id);
                // Skip rows that haven't actually changed — avoids pointless writes, and matters for
                // COMMITTEE rows specifically: an unchanged row could belong to a section the current
                // user can't even see (e.g. an assigned committee member editing guests/speakers only),
                // and the backend rejects any COMMITTEE write from such a user, even a no-op one.
                const originalItem = originalById.get(item.id);
                const unchanged = originalItem && JSON.stringify(this.toParticipantDTO(originalItem, type)) === JSON.stringify(dto);
                if (!unchanged) {
                    ops.push(this.eventApi.updateEventParticipant(this.eventId, item.id, dto));
                }
            } else {
                ops.push(this.eventApi.addEventParticipant(this.eventId, dto));
            }
        }

        for (const originalItem of original) {
            if (originalItem.id && !remainingIds.has(originalItem.id)) {
                ops.push(this.eventApi.deleteEventParticipant(this.eventId, originalItem.id));
            }
        }

        return ops;
    }

    private toParticipantDTO(item: any, type: EventParticipantType): EventParticipantDTO {
        if (type === 'SPEAKER') {
            return {
                participantType: type,
                name: item.name,
                title: item.role,
                company: item.company,
                topic: item.topic,
                topicDescription: item.topicDescription,
                tags: item.tags,
                photoUrl: item.photoUrl,
                videoUrl: item.videoUrl
            };
        }
        return {
            participantType: type,
            userId: type === 'COMMITTEE' ? (item.memberId || undefined) : undefined,
            name: item.name,
            title: item.title,
            shortDescription: item.shortDescription,
            photoUrl: item.photoUrl,
            videoUrl: item.videoUrl
        };
    }

    private saveAllParticipants(): Observable<any> {
        // Only ever attempt COMMITTEE writes when the caller can actually manage the roster (the
        // section is hidden from everyone else, so editEventCommittee is just what was loaded,
        // unmodified — submitting it anyway would 403 against the backend guard and, worse, abort
        // the other in-flight saves since forkJoin fails fast on the first error).
        const ops: Observable<any>[] = [
            ...(this.canManageCommitteeRoster ? this.buildParticipantOps(this.committeeAtLoad, this.editEventCommittee, 'COMMITTEE') : []),
            ...this.buildParticipantOps(this.guestsAtLoad, this.editEventGuestsOfHonor, 'GUEST_OF_HONOR'),
            ...this.buildParticipantOps(this.speakersAtLoad, this.editEventSpeakers, 'SPEAKER'),
        ];
        return ops.length ? forkJoin(ops) : of(null);
    }

    // Snapshots of editEvent*'s ids as they were right after load, used by buildParticipantOps to
    // detect which rows were removed in the edit form (present at load, missing on save = delete).
    private committeeAtLoad: (EventCommitteeMember & { id?: string })[] = [];
    private guestsAtLoad: (EventCommitteeMember & { id?: string })[] = [];
    private speakersAtLoad: (EventSpeaker & { id?: string })[] = [];

    ngOnInit(): void {
        this.eventId = this.route.snapshot.paramMap.get('id')!;
        this.setupRegistrationPaymentField();
        this.loadEvent();

        // The Registrations & Attendees tables live inside a Bootstrap accordion panel that's
        // collapsed (display:none) by default. DataTables can't measure column widths correctly
        // while its container is hidden, so a table initialized while collapsed renders broken/
        // empty — force a clean redraw once the panel is actually opened.
        setTimeout(() => {
            $('#collapseEventRegistrations').on('shown.bs.collapse', () => {
                this.refreshAllRegistrationTables();
            });
        });
    }

    ngOnDestroy(): void {
        this.committeeResizeObserver?.disconnect();
        this.guestResizeObserver?.disconnect();
        this.speakerResizeObserver?.disconnect();
        this.committeeSwiper?.destroy();
        this.guestSwiper?.destroy();
        this.speakerSwiper?.destroy();
        this.bookingsDtTrigger.unsubscribe();
        this.donationsDtTrigger.unsubscribe();
        this.regsDtTrigger.unsubscribe();
    }

    // Builds all three DataTables column configs once `event` (and therefore `canManageEvent`)
    // is known — the Method/Reference/Action admin-only columns depend on it, so building this
    // any earlier (e.g. ngAfterViewInit, before `event` loads) would under-build them even for
    // admins. Template refs are already resolved by this point regardless, since ngOnInit's
    // synchronous work always finishes — and with it Angular's own view-init pass — before any
    // async HTTP subscribe callback (this one included) can fire.
    private buildEventTablesConfig(): void {
        const isAdmin = this.canManageEvent;

        const bookingColumns: RanaDtOptions['columns'] = [
            {title: '#', data: null, orderable: false, render: (d: any, t: any, row: any, meta: any) => meta.row + 1},
            {title: 'Name', data: null, render: (d: any, t: any, row: any) => escapeHtml(this.bookingUserNames[row.userId] || row.userId)},
            {title: 'Option', data: null, render: (d: any, t: any, row: any) => escapeHtml(this.getBookingOptionsSummary(row))},
            {title: 'Attending', data: null, render: (d: any, t: any, row: any) => escapeHtml(this.formatAttending(row.attending))},
            ...(isAdmin && this.registrationQuestions.length > 0 ? [
                {title: 'Answers', data: null, render: (d: any, t: any, row: any) => escapeHtml(formatAnswers(row.formAnswers) || '-')}
            ] : []),
            {title: 'Registered On', data: null, render: (d: any, t: any, row: any) => escapeHtml(this.formatRegistrationDate(row.createdDate))},
            {title: 'Total', data: 'totalAmount', render: (d: any) => this.currencyPipe.transform(d, 'USD') || ''}
        ];
        if (isAdmin) {
            bookingColumns.push(
                {title: 'Paid', data: null, render: (d: any, t: any, row: any) => this.currencyPipe.transform(row.amountPaid ?? 0, 'USD') || ''},
                {title: 'Balance', data: null, orderable: false, render: (d: any, t: any, row: any) => this.balanceHtml(row)},
                {title: 'Method', data: null, render: (d: any, t: any, row: any) => escapeHtml(this.formatPaymentType(row.paymentType))},
                {title: 'Reference', data: null, render: (d: any, t: any, row: any) => escapeHtml(row.paymentReference || '-')},
                {title: 'Card payment', data: null, orderable: false, render: (d: any, t: any, row: any) => this.cardPaymentHtml(row)}
            );
        }
        bookingColumns.push({
            title: 'Status', data: 'status',
            render: (d: any, t: any, row: any) => `<span class="rana-badge ${this.paymentBadgeClass(row)}">${escapeHtml(this.paymentBadgeLabel(row))}</span>`
        });
        if (isAdmin) {
            bookingColumns.push({title: 'Action', data: null, orderable: false, ngTemplateRef: {ref: this.bookingActionTpl, context: {captureEvents: () => {}}}});
        }
        this.bookingsDtOptions = ranaDtOptions({columns: bookingColumns});

        this.donationsDtOptions = ranaDtOptions({
            columns: [
                {title: 'Name', data: null, render: (d: any, t: any, row: any) => escapeHtml(this.userNames[row.userId] || row.userId)},
                {title: 'Donation Amount', data: 'donationAmt', render: (d: any) => this.currencyPipe.transform(d) || ''},
                {
                    title: 'Status', data: 'status',
                    render: (d: any, t: any, row: any) => `<span class="rana-badge ${this.paymentBadgeClass(row)}">${escapeHtml(this.paymentBadgeLabel(row))}</span>`
                }
            ]
        });

        const regColumns: RanaDtOptions['columns'] = [
            {title: 'Name', data: null, render: (d: any, t: any, row: any) => escapeHtml(this.userNames[row.userId] || row.userId)},
            {title: 'Adults', data: 'adults'},
            {title: 'Children', data: 'children'},
            {title: 'Seniors', data: 'seniors'}
        ];
        if (isAdmin) {
            regColumns.push(
                {title: 'Email', data: null, className: 'text-truncate', render: (d: any, t: any, row: any) => escapeHtml(this.userEmails[row.userId] || '-')},
                {title: 'Phone', data: null, render: (d: any, t: any, row: any) => escapeHtml(this.userPhones[row.userId] || '-')},
                ...(this.registrationQuestions.length > 0 ? [
                    {title: 'Answers', data: null, render: (d: any, t: any, row: any) => escapeHtml(formatAnswers(row.formAnswers) || '-')}
                ] : []),
                {title: 'Method', data: null, render: (d: any, t: any, row: any) => escapeHtml(this.formatPaymentType(row.paymentType))},
                {title: 'Reference #', data: null, ngTemplateRef: {ref: this.refTpl, context: {captureEvents: () => {}}}},
                {title: 'Card payment', data: null, orderable: false, render: (d: any, t: any, row: any) => this.cardPaymentHtml(row)},
                {
                    title: 'Status', data: 'status',
                    render: (d: any, t: any, row: any) => `<span class="rana-badge ${this.paymentBadgeClass(row)}">${escapeHtml(this.paymentBadgeLabel(row))}</span>`
                },
                {
                    title: 'Source', data: 'registrationSource',
                    render: (d: any) => `<span class="rana-badge ${d === 'PUBLIC' ? 'rana-badge-info' : 'rana-badge-neutral'}">${d === 'PUBLIC' ? 'Public Link' : 'Portal'}</span>`
                },
                {title: 'Action', data: null, orderable: false, ngTemplateRef: {ref: this.regsActionTpl, context: {captureEvents: () => {}}}}
            );
        }
        this.regsDtOptions = ranaDtOptions({columns: regColumns});
    }

    // Array-level changes (initial load, batch name resolution, a confirm-payment replacing
    // `event`/`allBookings`) need an explicit retrigger; editing a row's own bindings inside an
    // already-created ngTemplateRef view (e.g. the Reference # input) does not. Tables B/C also
    // structurally appear/disappear with the registrationsTab (`*ngIf` on their wrapper divs),
    // so every refresh is deferred a tick for Angular to (re)create the <table> first.
    //
    // angular-datatables' own DataTableDirective.ngOnDestroy() calls `dtTrigger.unsubscribe()` —
    // directly on the Subject, not a subscription — which permanently closes it. Since these
    // three tables' containers toggle via *ngIf as the tab/category changes, the directive (and
    // with it, the shared trigger Subject) can get destroyed and killed by one code path (e.g. a
    // tab switch) moments before another (e.g. confirmPayment()'s success handler) tries to
    // refresh the same table — calling `.next()` on an already-closed Subject throws
    // ObjectUnsubscribedError. emitDtTrigger swaps in a fresh Subject whenever that happens
    // (safe: nothing is listening to a closed Subject anyway, and the next time the table mounts
    // its directive subscribes to whatever instance is current then), both ahead of time and as
    // a last-resort catch for the case where it closes in the gap between the check and the call.
    private emitDtTrigger(trigger: Subject<any>, assign: (s: Subject<any>) => void, getElement: () => DataTableDirective | undefined): void {
        if (trigger.closed) {
            trigger = new Subject();
            assign(trigger);
        }
        try {
            trigger.next(null);
        } catch {
            assign(new Subject());
            return;
        }
        // DataTables' own init can fail while its container is still hidden (e.g. the very
        // first trigger, which fires while the Registrations & Attendees accordion panel is
        // still collapsed) — displayTable() wraps that in a Promise, so the failure surfaces as
        // an unhandled rejection rather than a catchable throw here. The shown.bs.collapse
        // listener already re-triggers a clean rebuild once the panel is actually opened, so
        // this is just swallowing a known-benign, self-correcting background failure.
        getElement()?.dtInstance?.catch(() => {});
    }

    private dtRefreshQueued = {bookings: false, donations: false, regs: false};

    refreshBookingsTable(): void {
        this.bookingsDtOptions.data = this.bookingRowsForTab;
        // Several callers can ask for a redraw in the same moment (data loaded, panel shown, tab changed). Each
        // would destroy and re-init the table, and the second init hits an already-initialised table ("Cannot
        // reinitialise DataTable"). One queued redraw is enough: it reads the options.data set just above.
        if (this.dtRefreshQueued.bookings) return;
        this.dtRefreshQueued.bookings = true;
        // A small delay, not just a 0ms macrotask tick: Angular's own structural DOM changes to
        // this table (e.g. the <tfoot>'s *ngIf toggling with row count) happen on the same CD
        // cycle as the data change that triggered this refresh, but aren't guaranteed to finish
        // before a bare setTimeout(fn, 0) fires — racing DataTables' destroy()/reinit against
        // Angular still mutating the table's DOM corrupts DataTables' header detection.
        setTimeout(() => {
            this.dtRefreshQueued.bookings = false;
            const assign = (s: Subject<any>) => this.bookingsDtTrigger = s;
            if (this.bookingsDtElement?.dtInstance) {
                this.bookingsDtElement.dtInstance.then(dt => {
                    dt.destroy();
                    this.emitDtTrigger(this.bookingsDtTrigger, assign, () => this.bookingsDtElement);
                });
            } else {
                this.emitDtTrigger(this.bookingsDtTrigger, assign, () => this.bookingsDtElement);
            }
        }, 50);
    }

    refreshDonationsTable(): void {
        this.donationsDtOptions.data = this.donationRegistrations;
        // Several callers can ask for a redraw in the same moment (data loaded, panel shown, tab changed). Each
        // would destroy and re-init the table, and the second init hits an already-initialised table ("Cannot
        // reinitialise DataTable"). One queued redraw is enough: it reads the options.data set just above.
        if (this.dtRefreshQueued.donations) return;
        this.dtRefreshQueued.donations = true;
        setTimeout(() => {
            this.dtRefreshQueued.donations = false;
            const assign = (s: Subject<any>) => this.donationsDtTrigger = s;
            if (this.donationsDtElement?.dtInstance) {
                this.donationsDtElement.dtInstance.then(dt => {
                    dt.destroy();
                    this.emitDtTrigger(this.donationsDtTrigger, assign, () => this.donationsDtElement);
                });
            } else {
                this.emitDtTrigger(this.donationsDtTrigger, assign, () => this.donationsDtElement);
            }
        }, 50);
    }

    refreshRegsTable(): void {
        this.regsDtOptions.data = this.registrationRowsForTab;
        // Several callers can ask for a redraw in the same moment (data loaded, panel shown, tab changed). Each
        // would destroy and re-init the table, and the second init hits an already-initialised table ("Cannot
        // reinitialise DataTable"). One queued redraw is enough: it reads the options.data set just above.
        if (this.dtRefreshQueued.regs) return;
        this.dtRefreshQueued.regs = true;
        setTimeout(() => {
            this.dtRefreshQueued.regs = false;
            const assign = (s: Subject<any>) => this.regsDtTrigger = s;
            if (this.regsDtElement?.dtInstance) {
                this.regsDtElement.dtInstance.then(dt => {
                    dt.destroy();
                    this.emitDtTrigger(this.regsDtTrigger, assign, () => this.regsDtElement);
                });
            } else {
                this.emitDtTrigger(this.regsDtTrigger, assign, () => this.regsDtElement);
            }
        }, 50);
    }

    private refreshAllRegistrationTables(): void {
        if (this.isConferenceEvent) {
            this.refreshBookingsTable();
        } else {
            this.refreshDonationsTable();
            this.refreshRegsTable();
        }
    }

    // Re-syncs the committee/guest/speaker carousels' slide widths after every change-detection pass.
    // Fixes carousels that render with the wrong width on first load (this page has many async
    // API calls resolving after Swiper's initial measurement — a ResizeObserver on the container
    // alone isn't reliably triggered by all the layout shifts those cause), without needing a fixed delay.
    ngAfterViewChecked(): void {
        this.committeeSwiper?.update();
        this.guestSwiper?.update();
        this.speakerSwiper?.update();
    }

    get chapterRootLink(): string[] {
        return ['/chapter-home', this.userChapter || this.event?.chapterId || ''];
    }

    get isPaidEvent(): boolean {
        return (this.event?.eventType || '').toLowerCase() === 'paid';
    }

    get hasChildAgeLimitRule(): boolean {
        return this.isPaidEvent && this.getChildAgeMax() !== null;
    }

    get childrenCountLabel(): string {
        const minAge = this.getChildAgeMin();
        const maxAge = this.getChildAgeMax();
        if (this.hasChildAgeLimitRule && maxAge !== null) {
            return `Children (Ages ${minAge} to ${maxAge})`;
        }
        return 'Children';
    }

    get childrenCountGuidance(): string {
        const minAge = this.getChildAgeMin();
        const maxAge = this.getChildAgeMax();
        if (this.hasChildAgeLimitRule && maxAge !== null) {
            return ``;
        }
        return '';
    }

    get childAgesPlaceholder(): string {
        const minAge = this.getChildAgeMin();
        const maxAge = this.getChildAgeMax();
        if (this.hasChildAgeLimitRule && maxAge !== null) {
            return `Example: ${minAge},${minAge + 2},${maxAge}`;
        }
        return 'Example: 6,8,10';
    }

    get selectedPaymentType(): 'stripe' | 'zelle' | 'check' | 'later' {
        const raw = String(this.registrationForm.get('paymentType')?.value || 'later').toLowerCase();
        if (raw === 'zelle' || raw === 'check' || raw === 'later') {
            return raw;
        }
        return this.isStripeEnabled() ? 'stripe' : 'later';
    }

    isStripeEnabled(): boolean {
        return isStripePaymentEnabled(this.event);
    }

    // A saved 'stripe' choice falls back to 'later' once card payment is switched off for the event,
    // so the form never pre-selects a radio that is hidden.
    private availablePaymentType(paymentType: string | undefined): string {
        const normalized = String(paymentType || 'later').toLowerCase();
        return normalized === 'stripe' && !this.isStripeEnabled() ? 'later' : normalized;
    }

    get isOfflinePayment(): boolean {
        return this.selectedPaymentType === 'zelle' || this.selectedPaymentType === 'check';
    }

    get paymentReferenceLabel(): string {
        return this.selectedPaymentType === 'check' ? 'Check Number' : 'Zelle Confirmation Number';
    }

    get paymentSubmitLabel(): string {
        return this.selectedPaymentType === 'stripe'
            ? (this.userRegistration ? 'Update & Pay with Card' : 'Submit & Pay with Card')
            : (this.userRegistration ? 'Update Registration' : 'Submit Registration');
    }

    private setupRegistrationPaymentField(): void {
        const paymentReferenceCtrl = this.registrationForm.get('paymentReference');
        if (!paymentReferenceCtrl) {
            return;
        }
        paymentReferenceCtrl.setValidators([Validators.maxLength(100)]);
        paymentReferenceCtrl.updateValueAndValidity({emitEvent: false});
    }

    userNames: { [key: string]: string } = {};
    userEmails: { [key: string]: string } = {};
    userPhones: { [key: string]: string } = {};
    loadEvent(): void {
        this.spinner.show();
        this.eventApi.getEventById(this.eventId).pipe(
            finalize(() => this.spinner.hide())
        ).subscribe({
            next: (event) => {
                this.event = event;
                // Deferred: the ngTemplateRef columns (Reference #, Action) reference
                // <ng-template>s that sit behind *ngIf="!isConferenceEvent"/registrationsTab
                // checks — those only become true, and get instantiated by Angular, on the
                // change-detection cycle that follows this callback, not synchronously within
                // it. Reading this.refTpl/this.regsActionTpl before then would still see
                // whatever (likely undefined) value they held before `event` loaded.
                setTimeout(() => {
                    this.buildEventTablesConfig();
                    this.refreshAllRegistrationTables();
                });
                this.buildGroupedTicketOptions();
              //  if (event.chapterId === '0') {
                    this.fromEmailOptions = [ 'events@rajputra.org', 'nec@rajputra.org'];
              //  }
                this.eventForm.patchValue({
                    ...event,
                    additionalEmailIds: (event.additionalEmailIds || []).join(', '),
                    ccEmailIds: (event.ccEmailIds || []).join(', '),
                    stripePaymentEnabled: isStripePaymentEnabled(event)
                });
                this.buildEditPanels(event.ticketOptions || []);
                this.editQuestionsLegacy = event.registrationQuestions == null;
                this.editQuestionsDirty = false;
                this.editQuestions = event.registrationQuestions != null
                    ? JSON.parse(JSON.stringify(event.registrationQuestions))
                    : (event.eventCategory === 'CONFERENCE' ? [defaultAttendingQuestion()] : []);
                this.loadParticipants();
                this.loadChapterMembersForPicker();
                if (event.chapterId) {
                    this.chapterApiService.getChapterData(event.chapterId).subscribe({
                        next: (chapter) => this.eventChapterName = chapter?.name || '',
                        error: () => this.eventChapterName = ''
                    });
                }
                if (event.eventCategory === 'CONFERENCE') {
                    this.loadBookings();
                }
                // Fetch names for all registrants in a single batch request
                if (this.event.eventRegistrations?.length) {
                    const uniqueIds = [...new Set(
                        this.event.eventRegistrations.map((r: any) => r.userId).filter(Boolean)
                    )] as string[];
                    this.userApiService.getUsersBatch(uniqueIds).subscribe({
                        next: (users) => {
                            users.forEach((data: any) => {
                                const fullName = `${data.firstname} ${data.lastname}`;
                                this.userNames[data.id] = data.spouseName ? `${fullName} & ${data.spouseName}` : fullName;
                                this.userEmails[data.id] = data.email || '';
                                const phone = data.phone || '';
                                this.userPhones[data.id] = data.spousePhoneNumber ? `${phone}, ${data.spousePhoneNumber}` : phone;
                            });
                            this.refreshDonationsTable();
                            this.refreshRegsTable();
                        },
                        error: () => {}
                    });
                }
                this.initializePendingPaymentReferenceDrafts();

                // Existing logic for current user
                const existing = event.eventRegistrations?.find((r: any) => r.userId === this.userId);
                if (existing) {
                    this.userRegistration = existing;
                    this.localFormAnswers = existing.formAnswers ? JSON.parse(JSON.stringify(existing.formAnswers)) : [];
                    this.registrationForm.patchValue({
                        adults: existing.adults,
                        children: existing.children,
                        seniors: existing.seniors,
                        childAges: existing.childAges || '',
                        donationAmt: existing.donationAmt || 0,
                        paymentType: this.availablePaymentType(existing.paymentType),
                        paymentReference: existing.paymentReference || ''
                    });
                }
            },
            error: () => this.toastr.error('Failed to load event')
        });
    }

    pushNotification() {
        if (!confirm('Send this notification to all event participants?')) return;

        this.eventService.sendNotificationMessage(this.eventId)
            .subscribe({
                next: () => this.toastr.success('Notification sent'),
                error: () => this.toastr.error('Failed to send email')
            });
    }

    toggleEdit(): void {
        this.editEvent = !this.editEvent;
    }

    goBack(): void {
        this.location.back();
    }

    updateEvent(): void {
        if (this.eventForm.valid) {
            if (!(this.editQuestionsLegacy && !this.editQuestionsDirty)) {
                const cleaned = cleanQuestions(this.editQuestions);
                if (cleaned.error) {
                    this.toastr.warning(cleaned.error);
                    return;
                }
                this.editQuestions = cleaned.questions;
            }
            const formValue = this.eventForm.value;
            const additionalEmails = formValue.additionalEmailIds
                ? formValue.additionalEmailIds.split(',').map((e: string) => e.trim()).filter((e: string) => e)
                : [];
            const ccEmailIds = formValue.ccEmailIds
                ? formValue.ccEmailIds.split(',').map((e: string) => e.trim()).filter((e: string) => e)
                : [];
            const payload: any = {
                ...formValue,
                eventDescription: this.normalizeRichTextToHtml(formValue.eventDescription),
                additionalEmailIds: additionalEmails,
                ccEmailIds,
                updatedBy: this.userId,
                ticketOptions: this.editEventPanels.length > 0 ? this.flattenEditPanels() : undefined,
                // undefined = leave the event's questions unchanged (legacy event the organizer did not touch)
                registrationQuestions: this.editQuestionsLegacy && !this.editQuestionsDirty ? undefined : this.editQuestions
            };
            this.spinner.show();
            this.eventApi.updateEvent(this.eventId, payload).pipe(
                switchMap((res) => {
                    this.event = res;
                    return this.saveAllParticipants();
                }),
                finalize(() => this.spinner.hide())
            ).subscribe({
                next: () => {
                    this.toastr.success('Event updated successfully');
                    this.editEvent = false;
                    this.loadParticipants();
                },
                error: (err) => this.toastr.error(err?.error?.message || 'Failed to update event')
            });
        }
    }

    closeEvent(): void {
        this.spinner.show();
        this.eventApi.closeEvent(this.eventId).pipe(
            finalize(() => this.spinner.hide())
        ).subscribe({
            next: () => {
                this.toastr.success('Event closed successfully');
                this.event.status = "CLOSED";
            },
            error: () => this.toastr.error('Failed to close event')
        });
    }

    toggleRegister(): void {
        this.showRegister = !this.showRegister;
    }

    private getChildAgeMax(): number | null {
        const raw = this.event?.childAgeLimit;
        if (raw == null || raw === '') {
            return null;
        }
        const value = Number(raw);
        return Number.isFinite(value) && value >= 0 ? value : null;
    }

    private getChildAgeMin(): number {
        const raw = this.event?.childAgeMin;
        if (raw == null || raw === '') {
            return 5;
        }
        const value = Number(raw);
        if (!Number.isFinite(value) || value < 0) {
            return 5;
        }
        return Math.floor(value);
    }

    private getCount(controlName: 'adults' | 'children' | 'seniors'): number {
        const value = Number(this.registrationForm.get(controlName)?.value ?? 0);
        if (!Number.isFinite(value)) {
            return 0;
        }
        return Math.max(0, Math.floor(value));
    }

    // Public — the "Amount Due" breakdown template reads this directly.
    getDonationAmount(): number {
        const value = Number(this.registrationForm.get('donationAmt')?.value ?? 0);
        if (!Number.isFinite(value)) {
            return 0;
        }
        return Math.max(0, value);
    }

    // Per-category count x unit fee lines for the paid-event breakdown. getFeesSubtotal() sums these
    // same lines, so the breakdown can never disagree with Amount Due.
    get feeLines(): FeeLine[] {
        return buildFeeLines(
            this.event,
            {adults: this.getCount('adults'), children: this.getCount('children'), seniors: this.getCount('seniors')},
            {adults: 'Adults', children: this.childrenCountLabel, seniors: 'Senior Citizens'}
        );
    }

    // Per-person fee for a category (0 when none) — shown next to the count input labels.
    unitFee(category: FeeCategory): number {
        return getUnitFee(this.event, category);
    }

    trackFeeLine(_index: number, line: FeeLine): string {
        return line.category;
    }

    // Public — the registration-fees breakdown template reads this directly.
    getFeesSubtotal(): number {
        return sumFeeLines(this.feeLines);
    }

    // Early bird discount currently applicable to the fees subtotal (0 when no active discount).
    // Never applies to the donation amount.
    getDiscountAmount(): number {
        return calculateEventDiscount(this.event, this.getFeesSubtotal());
    }

    // True only while the event's discount date window is open — NOT tied to any particular
    // subtotal. Templates should additionally check getDiscountAmount()/getBookingDiscountAmount()
    // > 0 for the specific total they're displaying (fees vs. booking options can be zero
    // independently of each other, e.g. a CONFERENCE event has no per-person fees at all).
    isDiscountActive(): boolean {
        return isDiscountActive(this.event);
    }

    // True when the event has a configured, positive discount value — independent of the date
    // window (isDiscountActive()) and of any specific subtotal (getDiscountAmount()/
    // getBookingDiscountAmount()). Used to gate the informational "Early Bird Discount: ..."
    // note so it doesn't show "$0.00 off" once an admin zeroes out the value but leaves the
    // dates in place.
    hasDiscountValue(): boolean {
        const value = parseFloat(String(this.event?.discountValue ?? ''));
        return Number.isFinite(value) && value > 0;
    }

    // Renders the "YYYY-MM-DD" discountEndDate as "20th Sep" for the discount call-to-action text.
    formatDiscountEndDate(dateStr: string | null | undefined): string {
        if (!dateStr) return '';
        const parts = dateStr.split('-').map(Number);
        if (parts.length !== 3) return dateStr;
        const [year, month, day] = parts;
        const date = new Date(year, month - 1, day);
        if (isNaN(date.getTime())) return dateStr;
        const monthShort = date.toLocaleString('en-US', {month: 'short'});
        const suffix = (day > 3 && day < 21) ? 'th'
            : (day % 10 === 1) ? 'st'
            : (day % 10 === 2) ? 'nd'
            : (day % 10 === 3) ? 'rd' : 'th';
        return `${day}${suffix} ${monthShort}`;
    }

    calculateAmountDue(): number {
        const donation = this.getDonationAmount();
        const feesSubtotal = this.getFeesSubtotal();
        const discount = calculateEventDiscount(this.event, feesSubtotal);
        return feesSubtotal - discount + donation;
    }


    submitRegistration(): void {
        if (!this.registrationForm.valid) {
            this.registrationForm.markAllAsTouched();
            return;
        }

        const missingAnswers = missingRequiredLabels(this.registrationQuestions, this.localFormAnswers);
        if (missingAnswers.length > 0) {
            this.localSubmitAttempted = true;
            this.toastr.warning(`Please answer: ${missingAnswers.join(', ')}`);
            return;
        }

        const paymentType = this.selectedPaymentType;
        const paymentReference = String(this.registrationForm.get('paymentReference')?.value || '').trim();

        const adults = this.getCount('adults');
        const children = this.getCount('children');
        const seniors = this.getCount('seniors');
        const donation = this.getDonationAmount();
        const registration = {
            userId: this.userId,
            adults,
            children,
            seniors,
            childAges: String(this.registrationForm.get('childAges')?.value || '').trim(),
            donationAmt: donation,
            paymentType,
            paymentReference: paymentType === 'stripe' ? '' : paymentReference,
            status: 'PAYMENT_PENDING',
            formAnswers: pruneAnswers(this.registrationQuestions, this.localFormAnswers)
        };

        this.eventApi.updateEventRegistration(this.eventId, registration).subscribe({
            next: (updatedEvent) => {
                this.event = updatedEvent;
                this.refreshDonationsTable();
                this.refreshRegsTable();
                this.userRegistration = updatedEvent.eventRegistrations?.find((r: any) => r.userId === this.userId) || registration;
                this.initializePendingPaymentReferenceDrafts();
                sessionStorage.setItem('eventId', this.eventId);
                sessionStorage.setItem('eventRegistration', JSON.stringify(registration));
                sessionStorage.setItem('eventReturnPath', `/event-home/${this.eventId}`);

                if (paymentType === 'stripe') {
                    this.toastr.success('Registration saved. Redirecting to Stripe...');
                    this.payment(this.event.title, this.calculateAmountDue());
                    return;
                }

                this.showRegister = false;
                this.toastr.success('Registration saved. Payment status is pending until confirmed.');
            },
            error: (err) => this.toastr.error('Error registering: ' + (err?.error?.message || err?.message || err))
        });
    }

    private initializePendingPaymentReferenceDrafts(): void {
        this.pendingPaymentReferenceDrafts = {};
        this.pendingRegistrations.forEach((reg: any) => {
            this.pendingPaymentReferenceDrafts[reg.userId] = String(reg.paymentReference || '');
        });
    }

    isReferenceEditable(paymentType: string | undefined): boolean {
        const normalized = String(paymentType || '').toLowerCase();
        return normalized === 'zelle' || normalized === 'check';
    }

    formatTime(time: string): string {
        if (!time) return '';
        const [h, m] = time.split(':').map(Number);
        const period = h >= 12 ? 'PM' : 'AM';
        const hour = h % 12 || 12;
        return `${hour}:${m.toString().padStart(2, '0')} ${period}`;
    }

    formatPaymentType(paymentType: string | undefined): string {
        const normalized = String(paymentType || '').toLowerCase();
        if (normalized === 'stripe') return 'Card (Stripe)';
        if (normalized === 'zelle') return 'Zelle';
        if (normalized === 'check') return 'Check';
        if (normalized === 'later') return 'Pay Later';
        return 'Not Selected';
    }

    // Registration/booking timestamps come from the backend as "dd/MM/yyyy:HH:mm:ss"
    // (RANAUtil.getCurrentFormattedDate) rather than ISO, so Angular's date pipe can't
    // parse them directly — format here instead, matching ChapterEventComponent.parseDate.
    formatRegistrationDate(dateStr: string | null | undefined): string {
        if (!dateStr) return '—';
        const [datePart, timePart] = dateStr.split(':', 2);
        if (!datePart) return '—';
        const [day, month, year] = datePart.split('/').map(Number);
        if (!day || !month || !year) return '—';
        let hour = 0, minute = 0;
        if (timePart) {
            const [h, m] = timePart.split(':').map(Number);
            hour = Number.isFinite(h) ? h : 0;
            minute = Number.isFinite(m) ? m : 0;
        }
        const date = new Date(year, month - 1, day, hour, minute);
        if (isNaN(date.getTime())) return '—';
        return date.toLocaleString('en-US', {
            month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit'
        });
    }

    confirmPayment(reg: any): void {
        const updatedRegistration: any = {
            userId: reg.userId,
            adults: reg.adults,
            children: reg.children,
            seniors: reg.seniors,
            billableAdults: reg.billableAdults,
            billableChildren: reg.billableChildren,
            childAges: reg.childAges || '',
            donationAmt: reg.donationAmt || 0,
            paymentType: String(reg.paymentType || 'later').toLowerCase(),
            paymentReference: this.isReferenceEditable(reg.paymentType)
                ? String(this.pendingPaymentReferenceDrafts[reg.userId] || reg.paymentReference || '').trim()
                : String(reg.paymentReference || ''),
            status: 'PAYMENT_CONFIRMED'
        };

        this.eventApi.updateEventRegistration(this.eventId, updatedRegistration).subscribe({
            next: (updatedEvent) => {
                this.event = updatedEvent;
                this.refreshDonationsTable();
                this.refreshRegsTable();
                this.userRegistration = updatedEvent.eventRegistrations?.find((r: any) => r.userId === this.userId) || this.userRegistration;
                this.initializePendingPaymentReferenceDrafts();
                this.toastr.success('Payment confirmed successfully.');
            },
            error: (err) => this.toastr.error('Failed to confirm payment: ' + (err?.error?.message || err?.message || err))
        });
    }

    // ---- edit an existing registration (organizers, and the member on their own confirmed one) ----
    editDialog: { registration: any; mode: 'organizer' | 'member'; name: string } | null = null;

    // Chapter executive committee / NEC may waive a remaining balance; event-committee members may not.
    get canWaiveBalance(): boolean {
        return this.canManageCommitteeRoster;
    }

    // The member may change their own registration while the event is open and has not started.
    get memberCanEditConfirmed(): boolean {
        if (this.event?.status !== 'OPEN') return false;
        const start = this.event?.eventStartDate ? new Date(this.event.eventStartDate as any) : null;
        if (!start || isNaN(start.getTime())) return true;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        start.setHours(0, 0, 0, 0);
        return today < start;
    }

    // ---- "Didn't attend, no payment" ----

    statusDialog: { kind: 'booking' | 'registration'; row: any; name: string; restore: boolean } | null = null;
    statusDialogBusy = false;
    statusDialogError = '';

    openNoShow(row: any, kind: 'booking' | 'registration'): void {
        this.openStatusDialog(row, kind, false);
    }

    openRestoreNoShow(row: any, kind: 'booking' | 'registration'): void {
        this.openStatusDialog(row, kind, true);
    }

    private openStatusDialog(row: any, kind: 'booking' | 'registration', restore: boolean): void {
        if (kind === 'registration' && !row?.bookingId) {
            this.toastr.info('This registration was made before editing was available. Please re-save it from the member view first.');
            return;
        }
        const name = kind === 'booking' ? (this.bookingUserNames[row.userId] || '') : (this.userNames[row.userId] || '');
        this.statusDialogError = '';
        this.statusDialog = {kind, row, name, restore};
    }

    onStatusDialogConfirmed(reason: string): void {
        const d = this.statusDialog;
        if (!d || !this.event?.id) return;
        const bookingId = d.kind === 'booking' ? d.row.id : d.row.bookingId;
        const call = d.restore
            ? this.eventApi.restoreRegistrationNoShow(this.event.id, bookingId, reason)
            : this.eventApi.markRegistrationNoShow(this.event.id, bookingId, reason);
        this.statusDialogBusy = true;
        this.statusDialogError = '';
        call.pipe(finalize(() => this.statusDialogBusy = false)).subscribe({
            next: () => {
                this.toastr.success(d.restore ? 'Moved back to Payment Pending.' : 'Marked as didn\'t attend, no payment.');
                this.statusDialog = null;
                this.reloadRegistrations();
            },
            error: (err) => this.statusDialogError = err?.error?.message || err?.error?.error || err?.message || 'The change could not be saved.'
        });
    }

    openEditBooking(booking: EventBookingDTO): void {
        this.editDialog = {registration: booking, mode: 'organizer', name: this.bookingUserNames[booking.userId] || ''};
    }

    openEditRegistration(reg: any): void {
        if (!reg?.bookingId) {
            this.toastr.info('This registration was made before editing was available. Please re-save it from the member view first.');
            return;
        }
        this.editDialog = {registration: reg, mode: 'organizer', name: this.userNames[reg.userId] || ''};
    }

    openMemberEdit(reg: any): void {
        this.editDialog = {registration: reg, mode: 'member', name: ''};
    }

    onEditSaved(_booking: EventBookingDTO): void {
        this.editDialog = null;
        this.reloadRegistrations();
    }

    // Both kinds are bookings on the server; reload whichever view this event uses.
    private reloadRegistrations(): void {
        if (this.isConferenceEvent) {
            this.loadBookings();
        } else {
            this.loadEvent();
        }
    }

    /** Pays what is still owed on the member's own registration (the server quotes the balance, not the total). */
    payBalance(): void {
        sessionStorage.setItem('eventId', this.eventId);
        sessionStorage.setItem('eventReturnPath', `/event-home/${this.eventId}`);
        this.payment(this.event?.title || '', 0);
    }

    payment(eventName: string, amount: number): void {
        if (!this.isStripeEnabled()) {
            this.toastr.error('Card payments are not enabled for this event');
            return;
        }
        // Stripe flow: the server prices the checkout from the registration just saved; we only say who pays.
        const stripeRequest = {
            eventId: this.eventId,
            userId: this.userId
        };

        this.paymentApi.createEventCheckoutSession(stripeRequest).subscribe({
            next: (res) => {
                const stripe = (window as any).Stripe(res.publicKey);
                stripe.redirectToCheckout({sessionId: res.sessionId});
            },
            error: (err) => this.toastr.error(err.error?.error || err.error || 'Stripe checkout failed', 'Error')
        });
    }

    getRegistrationStatusLabel(): string {
        if (!this.userRegistration) return '';
        const status = this.userRegistration.status;
        if (status === 'PAYMENT_CONFIRMED') return 'Payment Confirmed';
        const paymentType = String(this.userRegistration.paymentType || '').toLowerCase();
        const ref = String(this.userRegistration.paymentReference || '').trim();
        if ((paymentType === 'zelle') && ref) {
            return 'Payment Acknowledge Pending';
        }
        return 'Payment Pending';
    }

    get registrationAlertClass(): string {
        if (!this.userRegistration) return '';
        if (this.userRegistration.status === 'PAYMENT_CONFIRMED') return 'alert-success';
        const paymentType = String(this.userRegistration.paymentType || '').toLowerCase();
        const ref = String(this.userRegistration.paymentReference || '').trim();
        if ((paymentType === 'zelle' || paymentType === 'check') && ref) {
            return 'alert-warning';
        }
        return 'alert-danger';
    }

    getRegistrationSummary(): string {
        if (!this.userRegistration) return '';
        const {adults, children, seniors, donationAmt} = this.userRegistration;

        const parts: string[] = [];
        if (adults > 0) parts.push(`${adults} adult${adults > 1 ? 's' : ''}`);
        if (children > 0) parts.push(`${children} child${children > 1 ? 'ren' : ''}`);
        if (seniors > 0) parts.push(`${seniors} senior${seniors > 1 ? 's' : ''}`);
        if (donationAmt && Number(donationAmt) > 0) parts.push(`Donation: $${donationAmt}`);
        return parts.length > 0 ? `You registered ${parts.join(', ')}` : 'You are registered.';
    }


    // Backend timestamps are "dd/MM/yyyy:HH:mm:ss" (RANAUtil.getCurrentFormattedDate), not ISO.
    // Missing dates (pre-dating this field) sort last, oldest.
    private parseRegistrationTimestamp(dateStr: string | null | undefined): number {
        if (!dateStr) return 0;
        const parts = dateStr.split(':');
        const datePart = parts[0];
        if (!datePart) return 0;
        const [day, month, year] = datePart.split('/').map(Number);
        if (!day || !month || !year) return 0;
        const hour = Number(parts[1]) || 0;
        const minute = Number(parts[2]) || 0;
        const second = Number(parts[3]) || 0;
        const date = new Date(year, month - 1, day, hour, minute, second);
        return isNaN(date.getTime()) ? 0 : date.getTime();
    }

    // Registrations an organizer marked "didn't attend, no payment" are kept out of every list, total and export
    // below and shown only in their own section (noShowRegistrations / noShowBookings).
    static readonly NO_SHOW = 'NO_SHOW_NO_PAYMENT';

    get activeRegistrations(): EventRegistrationDTO[] {
        return (this.event?.eventRegistrations || []).filter(r => r.status !== EventHomeComponent.NO_SHOW);
    }

    get activeBookings(): EventBookingDTO[] {
        return this.allBookings.filter(b => b.status !== EventHomeComponent.NO_SHOW);
    }

    get noShowRegistrations(): EventRegistrationDTO[] {
        return (this.event?.eventRegistrations || []).filter(r => r.status === EventHomeComponent.NO_SHOW)
            .sort((a, b) => this.parseRegistrationTimestamp(b.registrationDate) - this.parseRegistrationTimestamp(a.registrationDate));
    }

    get noShowBookings(): EventBookingDTO[] {
        return this.allBookings.filter(b => b.status === EventHomeComponent.NO_SHOW)
            .sort((a, b) => this.parseRegistrationTimestamp(b.createdDate) - this.parseRegistrationTimestamp(a.createdDate));
    }

    get noShowCount(): number {
        return this.isConferenceEvent ? this.noShowBookings.length : this.noShowRegistrations.length;
    }

    get sortedEventRegistrations(): EventRegistrationDTO[] {
        return [...this.activeRegistrations].sort((a, b) =>
            this.parseRegistrationTimestamp(b.registrationDate) - this.parseRegistrationTimestamp(a.registrationDate));
    }

    get sortedBookings(): EventBookingDTO[] {
        return [...this.activeBookings].sort((a, b) =>
            this.parseRegistrationTimestamp(b.createdDate) - this.parseRegistrationTimestamp(a.createdDate));
    }

    get confirmedRegistrations() {
        return this.sortedEventRegistrations.filter(r => r.status === 'PAYMENT_CONFIRMED');
    }

    get pendingRegistrations() {
        return this.sortedEventRegistrations.filter(r => r.status !== 'PAYMENT_CONFIRMED');
    }

    get totalAdults(): number {
        return this.activeRegistrations.reduce(
            (sum, r) => sum + Number(r.adults || 0),
            0
        ) ?? 0;
    }

    get totalChildren(): number {
        return this.activeRegistrations.reduce(
            (sum, r) => sum + Number(r.children || 0),
            0
        ) ?? 0;
    }

    get totalSeniors(): number {
        return this.activeRegistrations.reduce(
            (sum, r) => sum + Number(r.seniors || 0),
            0
        ) ?? 0;
    }

    get confirmedTotalAdults(): number {
        return this.confirmedRegistrations.reduce(
            (sum, r) => sum + Number(r.adults || 0),
            0
        );
    }

    get confirmedTotalChildren(): number {
        return this.confirmedRegistrations.reduce(
            (sum, r) => sum + Number(r.children || 0),
            0
        );
    }

    get adultFee(): number {
        return Number(this.event?.eventFeeAdult || 0);
    }

    get childFee(): number {
        return Number(this.event?.eventFeeChild || 0);
    }

    get totalDonations(): number {
        return this.activeRegistrations.reduce(
            (sum, r) => sum + Number(r.donationAmt || 0),
            0
        ) ?? 0;
    }

    get confirmedTotalDonations(): number {
        return this.confirmedRegistrations.reduce(
            (sum, r) => sum + Number(r.donationAmt || 0),
            0
        ) ?? 0;
    }

    get donationRegistrations() {
        return this.sortedEventRegistrations.filter(r => r.donationAmt && +r.donationAmt > 0);
    }

    get seniorFee(): number {
        return Number(this.event?.eventFeeSeniorCitizen || 0);
    }

    get confirmedTotalSeniors(): number {
        return this.confirmedRegistrations.reduce(
            (sum, r) => sum + Number(r.seniors || 0),
            0
        );
    }

    // Confirmed-registration fees at the event's current rates, plus donations from confirmed
    // registrations. Does not re-apply any early-bird discount window, so treat this as an
    // estimate for the KPI row, not a ledger figure.
    get confirmedRevenue(): number {
        return (this.confirmedTotalAdults * this.adultFee)
            + (this.confirmedTotalChildren * this.childFee)
            + (this.confirmedTotalSeniors * this.seniorFee)
            + this.confirmedTotalDonations;
    }

    // Registrations & Attendees tab filter — 'all' keeps every section visible (today's default).
    registrationsTab: 'all' | 'confirmed' | 'pending' | 'donations' = 'all';

    setRegistrationsTab(tab: 'all' | 'confirmed' | 'pending' | 'donations'): void {
        this.registrationsTab = tab;
        this.refreshAllRegistrationTables();
    }

    // Attendees + Pending Payment used to be two separate tables (a pending registrant appeared in
    // both, with a different slice of columns in each). One merged table, filtered by tab, instead.
    get registrationRowsForTab(): EventRegistrationDTO[] {
        if (this.registrationsTab === 'confirmed') return this.confirmedRegistrations;
        if (this.registrationsTab === 'pending') return this.pendingRegistrations;
        return this.sortedEventRegistrations;
    }

    get bookingRowsForTab(): EventBookingDTO[] {
        if (this.registrationsTab === 'confirmed') return this.sortedBookings.filter(b => b.status === 'PAYMENT_CONFIRMED');
        if (this.registrationsTab === 'pending') return this.pendingBookings;
        return this.sortedBookings;
    }

    // Whole-day count to the event's start date; negative once the event has started/passed.
    // eventStartDate is a plain "YYYY-MM-DD" string — parsed as its literal calendar date, not
    // through `new Date(str)`, which reads it as UTC midnight and shifts a day in any timezone
    // behind UTC (including every US timezone) once its local calendar fields are read back.
    get daysUntilEvent(): number | null {
        const raw = this.event?.eventStartDate;
        if (!raw) return null;
        let startDay: Date;
        if (raw instanceof Date) {
            startDay = new Date(raw.getFullYear(), raw.getMonth(), raw.getDate());
        } else {
            const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
            if (!match) return null;
            const [, year, month, day] = match;
            startDay = new Date(+year, +month - 1, +day);
        }
        const today = new Date();
        const todayDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        return Math.round((startDay.getTime() - todayDay.getTime()) / 86400000);
    }

    get daysUntilEventLabel(): string {
        const days = this.daysUntilEvent;
        if (days === null) return '—';
        if (days > 0) return `${days} day${days === 1 ? '' : 's'}`;
        if (days === 0) return 'Today';
        return 'Past event';
    }

    private normalizeRichTextToHtml(value: any): string {
        if (value == null || value === '') return '';
        return typeof value === 'string' ? value : toHTML(value);
    }

    // -------------------- CONFERENCE Booking --------------------

    get isConferenceEvent(): boolean {
        return this.event?.eventCategory === 'CONFERENCE';
    }

    get allTicketOptions(): EventTicketOption[] {
        return this.event?.ticketOptions || [];
    }

    groupedTicketOptions: { sectionLabel: string; options: EventTicketOption[] }[] = [];

    private buildGroupedTicketOptions(): void {
        const map = new Map<string, EventTicketOption[]>();
        const order: string[] = [];
        for (const opt of this.allTicketOptions) {
            const key = opt.sectionLabel || '';
            if (!map.has(key)) { map.set(key, []); order.push(key); }
            map.get(key)!.push(opt);
        }
        this.groupedTicketOptions = order.map(label => ({sectionLabel: label, options: map.get(label)!}));
    }

    getBookingOptionLabel(optionId: string): string {
        return this.event?.ticketOptions?.find(o => o.optionId === optionId)?.label || optionId;
    }

    // Ticket options are admin-defined free text (e.g. "Adult", "Children", "Bronze",
    // "Grand Sponser") rather than a fixed schema field, so headcounts are derived by
    // matching the resolved label against a keyword rather than a dedicated adults/children
    // field on the booking itself.
    private getBookingOptionQuantityByKeyword(booking: EventBookingDTO, keyword: string): number {
        return (booking.selectedOptions || []).reduce((sum, o) => {
            const label = this.getBookingOptionLabel(o.optionId).toLowerCase();
            return label.includes(keyword) ? sum + (o.quantity || 0) : sum;
        }, 0);
    }

    get hasSelectedPaidBookingOption(): boolean {
        return this.allTicketOptions.some(opt =>
            opt.paid !== false && (this.bookingQuantities[opt.optionId!] || 0) > 0);
    }

    get showBookingOptionsError(): boolean {
        return this.bookingSubmitAttempted && !this.hasSelectedPaidBookingOption;
    }

    get registrationQuestions(): EventFormQuestion[] {
        return this.event?.registrationQuestions || [];
    }

    // CONFERENCE events created before organizer-defined questions keep the built-in Yes/No attending radio.
    get usesLegacyAttending(): boolean {
        return this.isConferenceEvent && this.event?.registrationQuestions == null;
    }

    get showBookingAttendingError(): boolean {
        return this.bookingSubmitAttempted && !this.bookingAttending;
    }

    // Bookings made before the attending question was required have no answer.
    // ---- Stripe card payment status (organizers) ----

    loadCardPayments(): void {
        if (!this.canManageEvent) return;
        this.eventApi.getCardPayments(this.eventId).subscribe({
            next: (rows) => {
                this.cardPayments = rows;
                this.refreshAllRegistrationTables();
            },
            error: () => { /* organizers without access simply see no card column data */ }
        });
    }

    /** Newest card payment for a registration row (bookings match by booking id, LOCAL registrations by user). */
    cardPaymentFor(row: any): CardPaymentDTO | undefined {
        if (!row) return undefined;
        return this.cardPayments.find(p => (row.id && p.bookingId === row.id) || (!!row.userId && p.userId === row.userId && !p.bookingId))
            || this.cardPayments.find(p => !!row.userId && p.userId === row.userId);
    }

    cardPaymentLabel(p: CardPaymentDTO | undefined, row?: any): { text: string; css: string; detail: string } {
        const money = (c?: number) => this.currencyPipe.transform((c || 0) / 100, 'USD') || '';
        if (!p) {
            const card = String(row?.paymentType || '').toLowerCase() === 'stripe';
            if (!card) return {text: '—', css: '', detail: ''};
            // Confirmed card registrations without a Stripe record were confirmed before payments were tracked
            // (or by hand). Only an unconfirmed one was genuinely never sent to Stripe.
            return row?.status === 'PAYMENT_CONFIRMED'
                ? {text: 'No Stripe record', css: 'rana-badge-neutral',
                    detail: 'Confirmed before card payments were tracked, or by hand. Finance → Import will match it.'}
                : {text: 'No Stripe session', css: 'rana-badge-warning', detail: 'Card chosen but never sent to Stripe'};
        }
        const checked = p.lastSyncedAt ? 'checked ' + new Date(p.lastSyncedAt).toLocaleString() : 'not checked yet';
        switch (p.paymentStatus) {
            case 'PAID': return {text: 'Paid by card', css: 'rana-badge-success', detail: 'Stripe fee ' + money(p.stripeFee)};
            case 'PARTIALLY_REFUNDED': return {text: 'Part refunded', css: 'rana-badge-info', detail: money(p.amountRefunded) + ' returned'};
            case 'REFUNDED': return {text: 'Refunded', css: 'rana-badge-info', detail: money(p.amountRefunded) + ' returned'};
            case 'DISPUTED': return {text: 'Disputed', css: 'rana-badge-danger', detail: p.failureReason || ''};
            case 'PROCESSING': return {text: 'Bank processing', css: 'rana-badge-warning', detail: checked};
            case 'CANCELLED': return {text: 'Payer cancelled', css: 'rana-badge-neutral', detail: checked};
            case 'EXPIRED': return {text: 'Not paid (expired)', css: 'rana-badge-neutral', detail: checked};
            case 'FAILED': return {text: 'Card failed', css: 'rana-badge-danger', detail: p.failureReason || checked};
            default: return {text: 'Started, not paid', css: 'rana-badge-warning', detail: checked};
        }
    }

    cardPaymentHtml(row: any): string {
        const info = this.cardPaymentLabel(this.cardPaymentFor(row), row);
        if (!info.css) return '—';
        return `<span class="rana-badge ${info.css}">${escapeHtml(info.text)}</span>` +
            (info.detail ? `<div class="small text-muted">${escapeHtml(info.detail)}</div>` : '');
    }

    /** Asks Stripe about this row's card payment right now (read-only) and refreshes the page data. */
    checkStripe(row: any): void {
        const payment = this.cardPaymentFor(row);
        if (!payment) {
            this.toastr.info('This registration has no card payment recorded.');
            return;
        }
        this.spinner.show();
        this.eventApi.syncCardPayment(this.eventId, payment.id).pipe(finalize(() => this.spinner.hide())).subscribe({
            next: (updated) => {
                this.cardPayments = this.cardPayments.map(p => p.id === updated.id ? updated : p);
                this.toastr.success('Stripe says: ' + this.cardPaymentLabel(updated, row).text);
                // Stripe may have just confirmed the registration: reload what the page shows.
                if (this.isConferenceEvent) this.loadBookings(); else this.loadEvent();
                this.refreshAllRegistrationTables();
            },
            error: (err) => this.toastr.error(err?.error?.error || 'Could not check Stripe')
        });
    }

    hasCardPayment(row: any): boolean {
        return !!this.cardPaymentFor(row);
    }

    // Status shown for a registration/booking. Refunds (made by NEC from the Finance page or in the Stripe dashboard)
    // are shown explicitly; an unconfirmed card payment says so instead of looking like a plain "Pending".
    paymentBadgeLabel(row: any): string {
        if (row?.refundStatus === 'REFUNDED') return 'Refunded';
        if (row?.refundStatus === 'PARTIALLY_REFUNDED') return 'Part refunded';
        if (row?.status === 'PAYMENT_CONFIRMED') {
            if (row?.paymentState === 'BALANCE_DUE') return 'Balance due';
            if (row?.paymentState === 'OVERPAID') return 'Overpaid';
            return 'Confirmed';
        }
        return String(row?.paymentType || '').toLowerCase() === 'stripe' ? 'Awaiting card payment' : 'Pending';
    }

    /** Amount still owed (or overpaid) on a registration, for the organizers' table. */
    balanceHtml(row: any): string {
        const balance = row?.balance;
        if (balance == null || Math.abs(balance) < 0.005) return '—';
        const money = this.currencyPipe.transform(Math.abs(balance), 'USD') || '';
        return balance > 0
            ? `<span class="rana-badge rana-badge-warning">Due ${escapeHtml(money)}</span>`
            : `<span class="rana-badge rana-badge-info">Over ${escapeHtml(money)}</span>`;
    }

    paymentBadgeClass(row: any): string {
        if (row?.refundStatus) return 'rana-badge-info';
        if (row?.status === 'PAYMENT_CONFIRMED') {
            if (row?.paymentState === 'BALANCE_DUE') return 'rana-badge-warning';
            if (row?.paymentState === 'OVERPAID') return 'rana-badge-info';
            return 'rana-badge-success';
        }
        return 'rana-badge-warning';
    }

    // One-line summary of an attendee's answers for cards/summaries (empty when none).
    answersSummary(answers?: EventFormAnswer[]): string {
        return formatAnswers(answers);
    }

    formatAttending(attending?: string): string {
        return attending === 'YES' ? 'Yes' : attending === 'NO' ? 'No' : '-';
    }

    getBookingOptionsSummary(booking: EventBookingDTO): string {
        if (booking.selectedOptions && booking.selectedOptions.length > 0) {
            return booking.selectedOptions
                .filter(o => o.quantity > 0)
                .map(o => `${this.getBookingOptionLabel(o.optionId)} ×${o.quantity}`)
                .join(', ');
        }
        return this.getBookingOptionLabel(booking.ticketOptionId || '');
    }

    private downloadCsv(filename: string, headers: string[], rows: (string | number)[][]): void {
        const escapeCell = (value: string | number): string => {
            const str = String(value ?? '');
            return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
        };
        const lines = [headers, ...rows].map(row => row.map(escapeCell).join(','));
        const csvContent = lines.join('\r\n');
        const blob = new Blob([csvContent], {type: 'text/csv;charset=utf-8;'});
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();
        URL.revokeObjectURL(url);
    }

    // Admin export for the unified bookings table — mirrors its columns exactly (plus Email/Phone/
    // City/State, which the table itself omits for space) and respects whichever tab
    // (All/Confirmed/Pending) is currently active, same as the table it's exporting.
    exportPendingBookingsCsv(): void {
        const bookings = this.bookingRowsForTab;
        const questions = this.registrationQuestions;
        const headers = ['#', 'Name', 'Email', 'Phone', 'City', 'State', 'Option', 'Attending', ...questions.map(q => q.label), 'Registered On', 'Total', 'Method', 'Reference', 'Status'];
        const rows = bookings.map((bk, i) => [
            i + 1,
            this.bookingUserNames[bk.userId || ''] || bk.userId || '',
            this.bookingUserEmails[bk.userId || ''] || '',
            this.bookingUserPhones[bk.userId || ''] || '',
            this.bookingUserCities[bk.userId || ''] || '',
            this.bookingUserStates[bk.userId || ''] || '',
            this.getBookingOptionsSummary(bk),
            this.formatAttending(bk.attending),
            ...questions.map(q => answerLabelsFor(bk.formAnswers, q.questionId!)),
            this.formatRegistrationDate(bk.createdDate),
            bk.totalAmount ?? 0,
            this.formatPaymentType(bk.paymentType),
            bk.paymentReference || '',
            bk.status === 'PAYMENT_CONFIRMED' ? 'Confirmed' : 'Pending'
        ]);
        this.downloadCsv(`${this.event?.title || 'event'}-bookings.csv`, headers, rows);
    }

    // Admin export for the unified local-registrations table — mirrors its columns exactly and
    // respects whichever tab (All/Confirmed/Pending) is currently active, same as the table itself.
    exportPendingRegistrationsCsv(): void {
        const registrations = this.registrationRowsForTab;
        const questions = this.registrationQuestions;
        const headers = ['Name', 'Adults', 'Children', 'Seniors', 'Email', 'Phone', ...questions.map(q => q.label), 'Method', 'Reference', 'Status', 'Source'];
        const rows = registrations.map((reg: any) => [
            this.userNames[reg.userId] || reg.userId || '',
            reg.adults ?? 0,
            reg.children ?? 0,
            reg.seniors ?? 0,
            this.userEmails[reg.userId] || '',
            this.userPhones[reg.userId] || '',
            ...questions.map(q => answerLabelsFor(reg.formAnswers, q.questionId!)),
            this.formatPaymentType(reg.paymentType),
            reg.paymentReference || '',
            reg.status === 'PAYMENT_CONFIRMED' ? 'Confirmed' : 'Pending',
            reg.registrationSource === 'PUBLIC' ? 'Public Link' : 'Portal'
        ]);
        this.downloadCsv(`${this.event?.title || 'event'}-pending-payments.csv`, headers, rows);
    }

    exportDonationsCsv(): void {
        const isAdmin = this.canManageEvent;
        const headers = ['Name', ...(isAdmin ? ['Donation Amount', 'Status'] : [])];
        const rows = this.donationRegistrations.map((reg: any) => [
            this.userNames[reg.userId] || reg.userId || '',
            ...(isAdmin ? [reg.donationAmt ?? 0, reg.status === 'PAYMENT_CONFIRMED' ? 'Confirmed' : 'Pending'] : [])
        ]);
        this.downloadCsv(`${this.event?.title || 'event'}-donations.csv`, headers, rows);
    }

    get userBookingStatusLabel(): string {
        if (!this.userBooking) return '';
        return this.userBooking.status === 'PAYMENT_CONFIRMED' ? 'Payment Confirmed' : 'Payment Pending';
    }

    get userBookingAlertClass(): string {
        if (!this.userBooking) return '';
        return this.userBooking.status === 'PAYMENT_CONFIRMED' ? 'alert-success' : 'alert-warning';
    }

    get pendingBookings(): EventBookingDTO[] {
        return this.sortedBookings.filter(b => b.status !== 'PAYMENT_CONFIRMED');
    }

    get confirmedBookings(): EventBookingDTO[] {
        return this.sortedBookings.filter(b => b.status === 'PAYMENT_CONFIRMED');
    }

    get totalBookingAmount(): number {
        return this.activeBookings.reduce((sum, b) => sum + Number(b.totalAmount || 0), 0);
    }

    get totalBookingAdults(): number {
        return this.activeBookings.reduce((sum, b) => sum + this.getBookingOptionQuantityByKeyword(b, 'adult'), 0);
    }

    get totalBookingChildren(): number {
        return this.activeBookings.reduce((sum, b) => sum + this.getBookingOptionQuantityByKeyword(b, 'child'), 0);
    }

    // Public — the booking breakdown template reads this directly.
    getBookingOptionsSubtotal(): number {
        return this.allTicketOptions.reduce((sum, opt) => {
            const qty = this.bookingQuantities[opt.optionId!] || 0;
            const price = opt.paid === false ? 0 : (parseFloat(opt.price) || 0);
            return sum + price * qty;
        }, 0);
    }

    // Early bird discount currently applicable to the booking's ticket-option subtotal
    // (0 when no active discount). Never applies to the donation amount.
    getBookingDiscountAmount(): number {
        return calculateEventDiscount(this.event, this.getBookingOptionsSubtotal());
    }

    calculateBookingTotal(): number {
        const optionTotal = this.getBookingOptionsSubtotal();
        const discount = calculateEventDiscount(this.event, optionTotal);
        return optionTotal - discount + (this.bookingDonationAmt || 0);
    }

    loadBookings(): void {
        this.eventApi.getEventBookings(this.eventId).subscribe({
            next: (bookings) => {
                this.allBookings = bookings;
                this.refreshBookingsTable();
                this.userBooking = bookings.find(b => b.userId === this.userId) || null;
                if (this.userBooking) {
                    this.bookingQuantities = {};
                    if (this.userBooking.selectedOptions?.length) {
                        this.userBooking.selectedOptions.forEach(o => {
                            this.bookingQuantities[o.optionId] = o.quantity;
                        });
                    }
                    this.bookingDonationAmt = this.userBooking.donationAmt || 0;
                    this.bookingAttending = this.userBooking.attending || '';
                    this.bookingFormAnswers = this.userBooking.formAnswers ? JSON.parse(JSON.stringify(this.userBooking.formAnswers)) : [];
                    this.bookingPaymentType = this.availablePaymentType(this.userBooking.paymentType);
                    this.bookingPaymentReference = this.userBooking.paymentReference || '';
                }
                const ids = [...new Set(bookings.map(b => b.userId).filter(Boolean))] as string[];
                if (ids.length) {
                    this.userApiService.getUsersBatch(ids).subscribe({
                        next: (users) => {
                            users.forEach((u: any) => {
                                this.bookingUserNames[u.id] = u.spouseName
                                    ? `${u.firstname} ${u.lastname} & ${u.spouseName}`
                                    : `${u.firstname} ${u.lastname}`;
                                this.bookingUserEmails[u.id] = u.email || '';
                                const phone = u.phone || '';
                                this.bookingUserPhones[u.id] = u.spousePhoneNumber ? `${phone}, ${u.spousePhoneNumber}` : phone;
                                this.bookingUserCities[u.id] = u.city || '';
                                this.bookingUserStates[u.id] = u.state || '';
                            });
                            this.refreshBookingsTable();
                        },
                        error: () => {}
                    });
                }
            },
            error: () => {}
        });
    }

    getPublicEventLink(): string {
        return `${window.location.origin}/event-register/${this.eventId}`;
    }

    copyPublicLink(input: HTMLInputElement): void {
        input.select();
        navigator.clipboard.writeText(input.value).then(() => {
            this.toastr.success('Link copied to clipboard');
        }).catch(() => {
            this.toastr.info('Select the link and copy manually');
        });
    }

    toggleBookingForm(): void {
        this.showBookingForm = !this.showBookingForm;
        this.bookingSubmitAttempted = false;
        if (this.showBookingForm) {
            setTimeout(() => this.bookingFormSectionRef?.nativeElement?.scrollIntoView({behavior: 'smooth', block: 'start'}), 50);
        }
    }

    private getMissingRequiredGroups(options: EventTicketOption[], quantities: { [optionId: string]: number }): string[] {
        const groupLabels = new Set(
            options.filter(o => o.groupRequired).map(o => o.sectionLabel || '')
        );
        return Array.from(groupLabels).filter(label => {
            const groupOptions = options.filter(o => (o.sectionLabel || '') === label);
            return !groupOptions.some(o => (quantities[o.optionId!] || 0) > 0);
        });
    }

    submitBooking(): void {
        const missingRequired = this.allTicketOptions.filter(opt =>
            opt.required && !(this.bookingQuantities[opt.optionId!] > 0));
        if (missingRequired.length > 0) {
            this.toastr.warning(`Please enter a quantity for required option(s): ${missingRequired.map(o => o.label).join(', ')}`);
            return;
        }

        const missingRequiredGroups = this.getMissingRequiredGroups(this.allTicketOptions, this.bookingQuantities);
        if (missingRequiredGroups.length > 0) {
            this.toastr.warning(`Please select at least one option in: ${missingRequiredGroups.join(', ')}`);
            return;
        }

        if (!this.hasSelectedPaidBookingOption) {
            this.bookingSubmitAttempted = true;
            this.toastr.warning('Please select at least one of the option.');
            return;
        }

        if (this.usesLegacyAttending && !this.bookingAttending) {
            this.bookingSubmitAttempted = true;
            this.toastr.warning('Please select whether you are attending the conference.');
            return;
        }

        const missingAnswers = this.usesLegacyAttending
            ? []
            : missingRequiredLabels(this.registrationQuestions, this.bookingFormAnswers);
        if (missingAnswers.length > 0) {
            this.bookingSubmitAttempted = true;
            this.toastr.warning(`Please answer: ${missingAnswers.join(', ')}`);
            return;
        }

        const selectedOptions: EventBookingOption[] = Object.entries(this.bookingQuantities)
            .filter(([, qty]) => qty > 0)
            .map(([optionId, quantity]) => ({optionId, quantity}));
        const dto: EventBookingDTO = {
            eventId: this.eventId,
            userId: this.userId,
            selectedOptions,
            donationAmt: this.bookingDonationAmt,
            paymentType: this.bookingPaymentType as any,
            paymentReference: this.bookingPaymentType === 'stripe' ? '' : this.bookingPaymentReference,
            status: 'PAYMENT_PENDING',
            registrationSource: 'PORTAL',
            attending: this.usesLegacyAttending ? this.bookingAttending as 'YES' | 'NO' : undefined,
            formAnswers: this.usesLegacyAttending ? undefined : pruneAnswers(this.registrationQuestions, this.bookingFormAnswers)
        };
        const isUpdate = !!this.userBooking?.id;
        this.spinner.show();
        // Public endpoint (no EVENT:CREATE/UPDATE requirement) — this is a logged-in member
        // self-registering or editing their own booking, not a staff action. The backend
        // upserts by userId (createEventBooking finds the existing booking and updates it),
        // so the same call handles both first-time submission and edits.
        this.eventApi.createPublicEventBooking(this.eventId, dto).pipe(
            finalize(() => this.spinner.hide())
        ).subscribe({
            next: (b) => {
                this.userBooking = b;
                const idx = this.allBookings.findIndex(x => x.id === b.id);
                if (idx >= 0) this.allBookings[idx] = b; else this.allBookings.push(b);
                this.showBookingForm = false;
                if (this.bookingPaymentType === 'stripe') {
                    sessionStorage.setItem('eventId', this.eventId);
                    sessionStorage.setItem('eventReturnPath', `/event-home/${this.eventId}`);
                    this.toastr.success(`Booking ${isUpdate ? 'updated' : 'submitted'}. Redirecting to Stripe...`);
                    this.payment(this.event!.title, this.calculateBookingTotal());
                    return;
                }
                this.toastr.success(`Booking ${isUpdate ? 'updated' : 'submitted'}`);
            },
            error: () => this.toastr.error(`Failed to ${isUpdate ? 'update' : 'submit'} booking`)
        });
    }

    confirmBookingPayment(booking: EventBookingDTO): void {
        if (!booking.id) return;
        this.spinner.show();
        this.eventApi.updateEventBookingStatus(this.eventId, booking.id, 'PAYMENT_CONFIRMED').pipe(
            finalize(() => this.spinner.hide())
        ).subscribe({
            next: (updated) => {
                const idx = this.allBookings.findIndex(b => b.id === booking.id);
                if (idx >= 0) this.allBookings[idx] = updated;
                this.refreshBookingsTable();
                if (this.userBooking?.id === booking.id) this.userBooking = updated;
                this.toastr.success('Payment confirmed');
            },
            error: () => this.toastr.error('Failed to confirm payment')
        });
    }

    // -------------------- Speakers carousel --------------------
    private committeeSwiper: Swiper | null = null;
    private committeeResizeObserver: ResizeObserver | null = null;

    @ViewChild('committeeSwiperContainer') set committeeSwiperContainer(el: ElementRef<HTMLDivElement> | undefined) {
        if (el && !this.committeeSwiper) {
            setTimeout(() => {
                this.committeeSwiper = new Swiper(el.nativeElement, this.getCommitteeSwiperOptions());
                // Belt-and-suspenders: this page renders behind async data (bookings, chapter lookup, etc.)
                // that can resize the container well after Swiper's initial measurement. A ResizeObserver
                // catches that regardless of *when* it settles, unlike a one-shot delayed update().
                this.committeeResizeObserver = new ResizeObserver(() => this.committeeSwiper?.update());
                this.committeeResizeObserver.observe(el.nativeElement);
            });
        }
    }

    private getCommitteeSwiperOptions(): SwiperOptions {
        const memberCount = this.event?.eventCommittee?.length || 0;
        return {
            loop: memberCount > 3,
            observer: true,
            observeParents: true,
            spaceBetween: 20,
            speed: 1500,
            autoplay: {
                delay: 3000,
                disableOnInteraction: false,
                pauseOnMouseEnter: true
            },
            breakpoints: {
                0: {slidesPerView: 1, spaceBetween: 10},
                768: {slidesPerView: 2, spaceBetween: 20},
                992: {slidesPerView: 3, spaceBetween: 20}
            },
            pagination: {
                el: '.swiper-pagination',
                type: 'bullets',
                clickable: true
            }
        };
    }

    private guestSwiper: Swiper | null = null;
    private guestResizeObserver: ResizeObserver | null = null;

    @ViewChild('guestSwiperContainer') set guestSwiperContainer(el: ElementRef<HTMLDivElement> | undefined) {
        if (el && !this.guestSwiper) {
            setTimeout(() => {
                this.guestSwiper = new Swiper(el.nativeElement, this.getGuestSwiperOptions());
                this.guestResizeObserver = new ResizeObserver(() => this.guestSwiper?.update());
                this.guestResizeObserver.observe(el.nativeElement);
            });
        }
    }

    private getGuestSwiperOptions(): SwiperOptions {
        const guestCount = this.event?.eventGuestsOfHonor?.length || 0;
        return {
            loop: guestCount > 3,
            observer: true,
            observeParents: true,
            spaceBetween: 20,
            speed: 1500,
            autoplay: {
                delay: 3000,
                disableOnInteraction: false,
                pauseOnMouseEnter: true
            },
            breakpoints: {
                0: {slidesPerView: 1, spaceBetween: 10},
                768: {slidesPerView: 2, spaceBetween: 20},
                992: {slidesPerView: 3, spaceBetween: 20}
            },
            pagination: {
                el: '.swiper-pagination',
                type: 'bullets',
                clickable: true
            }
        };
    }

    private speakerSwiper: Swiper | null = null;
    private speakerResizeObserver: ResizeObserver | null = null;

    @ViewChild('speakerSwiperContainer') set speakerSwiperContainer(el: ElementRef<HTMLDivElement> | undefined) {
        if (el && !this.speakerSwiper) {
            setTimeout(() => {
                this.speakerSwiper = new Swiper(el.nativeElement, this.getSpeakerSwiperOptions());
                this.speakerResizeObserver = new ResizeObserver(() => this.speakerSwiper?.update());
                this.speakerResizeObserver.observe(el.nativeElement);
            });
        }
    }

    private getSpeakerSwiperOptions(): SwiperOptions {
        const speakerCount = this.event?.eventSpeakers?.length || 0;
        return {
            loop: speakerCount > 3,
            observer: true,
            observeParents: true,
            spaceBetween: 20,
            speed: 1500,
            autoplay: {
                delay: 3000,
                disableOnInteraction: false,
                pauseOnMouseEnter: true
            },
            breakpoints: {
                0: {slidesPerView: 1, spaceBetween: 10},
                768: {slidesPerView: 2, spaceBetween: 20},
                992: {slidesPerView: 3, spaceBetween: 20}
            },
            pagination: {
                el: '.swiper-pagination',
                type: 'bullets',
                clickable: true
            }
        };
    }

    getInitials(name: string | undefined): string {
        if (!name) return '?';
        const parts = name.trim().split(/\s+/);
        const initials = parts.length > 1 ? parts[0][0] + parts[parts.length - 1][0] : parts[0].slice(0, 2);
        return initials.toUpperCase();
    }

}
